import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Ban,
  Building2,
  Check,
  CheckCircle2,
  ListChecks,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Power,
  RefreshCw,
  Search,
  SearchX,
  ShieldCheck,
  SlidersHorizontal,
  Tag,
  Tags,
  Users,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { ApiError, apiErrorMessage, getAdminToken, setAdminToken } from '../../api/client'
import { roleLabel } from '../../auth/roleOptions'
import { AdminHeader } from './adminAccess'
import { AdminHero, HeroBadge } from './AdminHero'
import { adminApi } from '../../api/endpoints'
import { ChipGroup } from '../../components/ChipGroup'
import { ErrorMessage } from '../../components/ErrorMessage'
import { StateSelect } from '../../components/StateSelect'
import { useTaxonomy } from '../../taxonomy/TaxonomyContext'
import type {
  AdminProviderResponse,
  AdminProviderUpdateRequest,
  NeedCategory,
  VerificationStatus,
} from '../../types/api'

/** Unverified also finds DOCS_DECLARED - the server treats the two alike for this filter. */
const STATUS_OPTIONS: { label: string; value: VerificationStatus | '' }[] = [
  { label: 'Any status', value: '' },
  { label: 'Verified', value: 'VERIFIED' },
  { label: 'Unverified', value: 'UNVERIFIED' },
  { label: 'Suspended', value: 'SUSPENDED' },
]

/** DOCS_DECLARED is not a separate state to ops any more - it reads as unverified. */
const shownStatus = (s: VerificationStatus) => (s === 'DOCS_DECLARED' ? 'UNVERIFIED' : s)

/** A sentinel in the Category/Sub Category filters, not a real taxonomy value - see load(). */
const UNASSIGNED = '__unassigned__'

type SortKey =
  | 'fullName' | 'email' | 'phone' | 'address' | 'city' | 'state' | 'pincode'
  | 'category' | 'subCategory' | 'status'

/**
 * What the table draws: related fields share a column (contact, location), so each column is wide
 * enough that an email or a phone number does not break mid-word. Each sorts by its lead field.
 */
const TABLE_COLUMNS: { sortKey: SortKey; label: string; width: string }[] = [
  { sortKey: 'fullName', label: 'Provider', width: 'w-[19%]' },
  { sortKey: 'email', label: 'Contact', width: 'w-[21%]' },
  { sortKey: 'city', label: 'Location', width: 'w-[18%]' },
  { sortKey: 'category', label: 'Category', width: 'w-[15%]' },
  { sortKey: 'subCategory', label: 'Sub category', width: 'w-[15%]' },
  { sortKey: 'status', label: 'Status', width: 'w-[12%]' },
]

const STATUS_STYLE: Record<string, { pill: string; dot: string; label: string }> = {
  VERIFIED: { pill: 'bg-emerald-50 text-emerald-700 ring-emerald-200', dot: 'bg-emerald-500', label: 'Verified' },
  UNVERIFIED: { pill: 'bg-amber-50 text-amber-800 ring-amber-200', dot: 'bg-amber-500', label: 'Unverified' },
  SUSPENDED: { pill: 'bg-rose-50 text-rose-700 ring-rose-200', dot: 'bg-rose-500', label: 'Suspended' },
}

const AVATAR_TONES = [
  'from-sky-500 to-blue-600',
  'from-orange-500 to-rose-500',
  'from-emerald-500 to-teal-500',
  'from-violet-500 to-purple-600',
  'from-amber-400 to-orange-500',
  'from-pink-500 to-fuchsia-500',
]

/** The same colour for the same name, every time. */
function avatarTone(name: string): string {
  let h = 0
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) | 0
  return AVATAR_TONES[Math.abs(h) % AVATAR_TONES.length]
}

const initials = (name: string) =>
  name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('') || '?'

/** Every searchable field - the quick search looks through all of them. */
const COLUMNS: { key: SortKey; label: string }[] = [
  { key: 'fullName', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'address', label: 'Address' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'pincode', label: 'Pincode' },
  { key: 'category', label: 'Category' },
  { key: 'subCategory', label: 'Sub category' },
  { key: 'status', label: 'Status' },
]

/** What a row shows in a column, as one string - used for both sorting and the quick search. */
function cellText(r: AdminProviderResponse, key: SortKey): string {
  switch (key) {
    case 'fullName': return r.fullName
    case 'email': return r.email ?? ''
    case 'phone': return r.phone ?? ''
    case 'address': return r.address ?? ''
    case 'city': return r.city ?? ''
    case 'state': return r.state ?? ''
    case 'pincode': return r.pincode ?? ''
    case 'category': return r.categories.join(', ')
    case 'subCategory': return r.subCategories.join(', ')
    case 'status': return shownStatus(r.verificationStatus)
  }
}

export function AdminProvidersPage() {
  const navigate = useNavigate()
  const { categories, labelOf, itemsOf } = useTaxonomy()

  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [category, setCategory] = useState('')
  const [subCategory, setSubCategory] = useState('')
  const [subCategoryUnassigned, setSubCategoryUnassigned] = useState(false)
  const [status, setStatus] = useState<VerificationStatus | ''>('')

  const [rows, setRows] = useState<AdminProviderResponse[]>([])
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  // Quick search and column sort run on the rows already loaded; the filter form above is what
  // asks the server for a different set.
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' } | null>(null)

  const visibleRows = useMemo(() => {
    const needle = search.trim().toLowerCase()
    let out = rows
    if (needle) {
      out = out.filter((r) => COLUMNS.some((c) => cellText(r, c.key).toLowerCase().includes(needle)))
    }
    if (sort) {
      const { key, dir } = sort
      out = [...out].sort((a, b) => {
        const x = cellText(a, key)
        const y = cellText(b, key)
        // An empty cell sorts last in either direction, so "Not set" never crowds the top.
        if (!x && !y) return 0
        if (!x) return 1
        if (!y) return -1
        const c = x.localeCompare(y, undefined, { numeric: true, sensitivity: 'base' })
        return dir === 'asc' ? c : -c
      })
    }
    return out
  }, [rows, search, sort])

  const statusCounts = useMemo(() => {
    const out = { VERIFIED: 0, UNVERIFIED: 0, SUSPENDED: 0 }
    for (const r of rows) out[shownStatus(r.verificationStatus) as keyof typeof out]++
    return out
  }, [rows])

  const [editing, setEditing] = useState<AdminProviderResponse | null>(null)
  const [assigningCategory, setAssigningCategory] = useState(false)
  const [bulkCategory, setBulkCategory] = useState<NeedCategory>('ACADEMICS')
  const [bulkItems, setBulkItems] = useState<string[]>([])

  const signOut = useCallback(() => {
    setAdminToken(null)
    navigate('/admin/login', { replace: true })
  }, [navigate])

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    adminApi
      .providers({
        city,
        state,
        category: category === UNASSIGNED ? undefined : category,
        categoryUnassigned: category === UNASSIGNED,
        subCategory,
        subCategoryUnassigned,
        status: status || undefined,
      })
      .then((res) => {
        setRows(res)
        // Selections from a previous filter/refresh do not carry over cleanly - a row that
        // scrolled out of the new result set would still show "selected" with nothing to act on.
        setSelected(new Set())
      })
      .catch((err) => {
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          signOut()
          return
        }
        setError(apiErrorMessage(err, 'Could not load the providers directory'))
      })
      .finally(() => setLoading(false))
    // Every filter is a dependency: load() must read the current filter state when the Search
    // button (or Refresh, or a bulk action's own follow-up) calls it, not whatever the values were
    // when this closure was first created. This does not cause a fetch on every keystroke - nothing
    // calls load() from an onChange, only the explicit actions above.
  }, [signOut, city, state, category, subCategory, subCategoryUnassigned, status])

  useEffect(() => {
    if (getAdminToken()) load()
    // Only on mount - Search is a deliberate action, not a retrigger on every keystroke. load()
    // itself changes identity when a filter changes (see its own dependency array above), but that
    // is not the same as this effect re-running - this effect still only fires once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!getAdminToken()) return <Navigate to="/admin/login" replace />

  function toggleOne(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // "Select all" means every row currently shown, so a search never lets a bulk action reach a
  // row the admin cannot see.
  const allVisibleSelected = visibleRows.length > 0 && visibleRows.every((r) => selected.has(r.id))

  function toggleAll() {
    setSelected(allVisibleSelected ? new Set() : new Set(visibleRows.map((r) => r.id)))
  }

  function changeSearch(value: string) {
    setSearch(value)
    const needle = value.trim().toLowerCase()
    if (!needle) return
    const stillShown = new Set(
      rows.filter((r) => COLUMNS.some((c) => cellText(r, c.key).toLowerCase().includes(needle))).map((r) => r.id),
    )
    setSelected((prev) => new Set([...prev].filter((id) => stillShown.has(id))))
  }

  function sortBy(key: SortKey) {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, dir: 'asc' }
      return prev.dir === 'asc' ? { key, dir: 'desc' } : null
    })
  }

  function reportOutcome(action: string, res: { succeeded: number; failed: { participantId: string; error: string }[] }) {
    if (res.failed.length === 0) {
      setNotice(`${action}: ${res.succeeded} provider${res.succeeded === 1 ? '' : 's'} updated.`)
    } else {
      setNotice(
        `${action}: ${res.succeeded} updated, ${res.failed.length} failed - `
          + res.failed.map((f) => f.error).slice(0, 3).join('; '),
      )
    }
    load()
  }

  async function bulkApprove() {
    if (selected.size === 0) return
    setBusy(true)
    setError(null)
    try {
      const res = await adminApi.bulkApprove({ participantIds: [...selected] })
      reportOutcome('Bulk approve', res)
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not approve the selected providers'))
    } finally {
      setBusy(false)
    }
  }


  async function bulkActivate() {
    if (selected.size === 0) return
    if (!confirm(`Activate ${selected.size} provider${selected.size === 1 ? '' : 's'}? Suspended ones are reinstated and put back on the Signals-DPG network; anyone not suspended is left as they are.`)) return
    setBusy(true)
    setError(null)
    try {
      const res = await adminApi.bulkActivate([...selected])
      reportOutcome('Bulk activate', res)
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not activate the selected providers'))
    } finally {
      setBusy(false)
    }
  }

  async function bulkDeactivate() {
    if (selected.size === 0) return
    if (!confirm(`Suspend ${selected.size} provider${selected.size === 1 ? '' : 's'}? They will lose the Verified badge.`)) return
    setBusy(true)
    setError(null)
    try {
      const res = await adminApi.bulkDeactivate([...selected])
      reportOutcome('Bulk deactivate', res)
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not deactivate the selected providers'))
    } finally {
      setBusy(false)
    }
  }

  async function applyBulkCategory() {
    if (selected.size === 0) return
    setBusy(true)
    setError(null)
    try {
      const res = await adminApi.bulkAssignCategory({
        participantIds: [...selected],
        category: bulkCategory,
        // Left out when none are picked, so a provider's existing sub categories are not touched.
        items: bulkItems.length > 0 ? bulkItems : undefined,
      })
      reportOutcome('Bulk category assignment', res)
      setAssigningCategory(false)
      setBulkItems([])
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not assign that category to the selected providers'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="admin-console min-h-screen bg-ink-100 lg:pl-64">
      <AdminHeader current="providers" onSignOut={signOut} width="max-w-7xl">
        <button onClick={load} aria-label="Refresh" className="btn-ghost gap-1.5">
          <RefreshCw size={14} /> Refresh
        </button>
      </AdminHeader>

      <main className="mx-auto max-w-[100rem] space-y-5 px-4 py-6">
        <AdminHero
          icon={<ListChecks size={24} />}
          eyebrow="Provider report"
          title="Providers directory"
          blurb="Every provider, whatever their state. Filter, select, and approve or tag a batch at once."
        >
          <HeroBadge>
            <Users size={13} className="text-sky-600" /> {rows.length.toLocaleString()} shown
          </HeroBadge>
          {(['VERIFIED', 'UNVERIFIED', 'SUSPENDED'] as const).map((s) => (
            <HeroBadge key={s}>
              <span className={`h-2 w-2 rounded-full ${STATUS_STYLE[s].dot}`} />
              {statusCounts[s].toLocaleString()} {STATUS_STYLE[s].label.toLowerCase()}
            </HeroBadge>
          ))}
        </AdminHero>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            load()
          }}
          className="rounded-2xl border border-ink-200/70 bg-white p-4 shadow-card"
        >
          <div className="mb-3 flex items-center gap-2 text-sm font-bold text-ink-900">
            <SlidersHorizontal size={15} className="text-amber-600" /> Filters
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6 lg:items-end">
            <FilterField label="City" icon={<Building2 size={14} />}>
              <input value={city} onChange={(e) => setCity(e.target.value)} className="input w-full !pl-9" placeholder="e.g. Patna" />
            </FilterField>
            <FilterField label="State" icon={<MapPin size={14} />}>
              <input value={state} onChange={(e) => setState(e.target.value)} className="input w-full !pl-9" placeholder="e.g. Bihar" />
            </FilterField>
            <FilterField label="Category" icon={<Tags size={14} />}>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className="input w-full !pl-9">
                <option value="">Any category</option>
                <option value={UNASSIGNED}>Unassigned (no category)</option>
                {categories.map((c) => (
                  <option key={c.name} value={labelOf(c.name)}>{labelOf(c.name)}</option>
                ))}
              </select>
            </FilterField>
            <FilterField label="Sub category" icon={<Tag size={14} />}>
              <input
                value={subCategory}
                onChange={(e) => setSubCategory(e.target.value)}
                disabled={subCategoryUnassigned}
                className="input w-full !pl-9 disabled:opacity-50"
                placeholder="e.g. CAT"
              />
            </FilterField>
            <FilterField label="Status" icon={<ShieldCheck size={14} />}>
              <select value={status} onChange={(e) => setStatus(e.target.value as VerificationStatus | '')} className="input w-full !pl-9">
                {STATUS_OPTIONS.map((s) => <option key={s.label} value={s.value}>{s.label}</option>)}
              </select>
            </FilterField>
            <button type="submit" className="btn-sun w-full gap-1.5">
              <Search size={15} /> Search
            </button>
          </div>
          <label className="mt-3 inline-flex items-center gap-1.5 text-xs text-ink-500">
            <input
              type="checkbox"
              checked={subCategoryUnassigned}
              onChange={(e) => setSubCategoryUnassigned(e.target.checked)}
            />
            Unassigned only (sub category)
          </label>
        </form>

        {notice && (
          <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
            <CheckCircle2 size={16} className="shrink-0" /> {notice}
          </div>
        )}
        <ErrorMessage message={error} />

        {selected.size > 0 && (
          <div className="sticky top-[4.5rem] z-10 flex flex-wrap items-center gap-2 rounded-xl border border-sun-400/60 bg-sun-300/30 px-3 py-2.5 shadow-card backdrop-blur">
            <span className="text-sm font-semibold text-ink-900">{selected.size} selected</span>
            <button onClick={bulkApprove} disabled={busy} className="btn-sun !px-3 !py-1.5 text-xs gap-1.5">
              <Check size={13} /> Approve
            </button>
            <button
              onClick={bulkActivate}
              disabled={busy}
              title="Reinstates suspended providers and puts them back on the network. Anyone not suspended is left as they are."
              className="btn-ghost !px-3 !py-1.5 text-xs gap-1.5 !text-emerald-700 hover:!bg-emerald-50"
            >
              <Power size={13} /> Activate
            </button>
            <button
              onClick={bulkDeactivate}
              disabled={busy}
              className="btn-ghost !px-3 !py-1.5 text-xs gap-1.5 !text-red-700 hover:!bg-red-50"
            >
              <Ban size={13} /> Deactivate
            </button>
            <button
              onClick={() => setAssigningCategory((v) => !v)}
              disabled={busy}
              className="btn-ghost !px-3 !py-1.5 text-xs gap-1.5"
            >
              <ListChecks size={13} /> Assign category...
            </button>
          </div>
        )}

        {assigningCategory && selected.size > 0 && (
          <div className="space-y-3 rounded-2xl border border-ink-200/70 bg-white p-4 shadow-card">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-ink-900">
                Assign a Category and Sub Category to {selected.size} selected provider{selected.size === 1 ? '' : 's'}
              </span>
              <button onClick={() => setAssigningCategory(false)} aria-label="Close" className="text-ink-400 hover:text-ink-700">
                <X size={16} />
              </button>
            </div>
            <p className="text-xs text-ink-500">
              Makes this the primary category of each selected provider. Sub categories you pick are
              set; if you pick none, any they already have are kept, as are their modes, timings and
              fees. Use this to fix a batch that landed with no category recorded (a bulk import,
              typically).
            </p>
            <label className="block">
              <span className="label label-required">Category</span>
              <select
                value={bulkCategory}
                onChange={(e) => { setBulkCategory(e.target.value as NeedCategory); setBulkItems([]) }}
                className="input !w-64"
              >
                {categories.map((c) => (
                  <option key={c.name} value={c.name}>{labelOf(c.name)}</option>
                ))}
              </select>
            </label>
            <ChipGroup
              label="Sub category (optional)"
              options={itemsOf(bulkCategory)}
              values={bulkItems}
              onToggle={(v) => setBulkItems((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]))}
            />
            <button
              onClick={applyBulkCategory}
              disabled={busy}
              className="btn-sun"
            >
              {busy ? 'Applying...' : `Apply to ${selected.size} provider${selected.size === 1 ? '' : 's'}`}
            </button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center gap-2 rounded-2xl bg-white p-6 text-sm text-ink-500 shadow-card">
            <Loader2 size={16} className="animate-spin" /> Loading providers...
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-2 rounded-2xl bg-white p-10 text-center shadow-card">
            <span className="grid h-12 w-12 place-items-center rounded-full bg-ink-100 text-ink-400"><SearchX size={22} /></span>
            <div className="font-semibold text-ink-900">No providers match these filters</div>
            <div className="text-sm text-ink-500">Loosen a filter and search again.</div>
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-ink-200/70 bg-white shadow-card">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-ink-100 px-4 py-3">
              <span className="text-sm text-ink-600">
                <span className="font-bold text-ink-900">
                  {search.trim() ? `${visibleRows.length} of ${rows.length}` : rows.length.toLocaleString()}
                </span>{' '}
                provider{rows.length === 1 ? '' : 's'}
                <span className="hidden text-ink-400 md:inline"> - click a heading to sort, a name to edit</span>
              </span>
              <label className="relative block w-full sm:w-80">
                <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
                <input
                  value={search}
                  onChange={(e) => changeSearch(e.target.value)}
                  className="input w-full !rounded-full !py-2 !pl-9"
                  placeholder="Search name, email, phone, city, category..."
                  aria-label="Search the results"
                />
              </label>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full min-w-[960px] table-fixed text-left text-[13px]">
                <thead className="bg-ink-50/80 text-[11px] font-bold uppercase tracking-wide text-ink-500">
                  <tr>
                    <th className="w-11 px-4 py-3">
                      <input type="checkbox" checked={allVisibleSelected} onChange={toggleAll} aria-label="Select all" />
                    </th>
                    {TABLE_COLUMNS.map((c) => {
                      const active = sort?.key === c.sortKey
                      const Icon = !active ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown
                      return (
                        <th
                          key={c.label}
                          aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                          className={`px-3 py-3 ${c.width}`}
                        >
                          <button
                            type="button"
                            onClick={() => sortBy(c.sortKey)}
                            className={`inline-flex items-center gap-1 uppercase hover:text-ink-900 ${active ? 'text-amber-700' : ''}`}
                          >
                            {c.label}
                            <Icon size={11} className={active ? '' : 'opacity-40'} />
                          </button>
                        </th>
                      )
                    })}
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-100">
                  {visibleRows.length === 0 && (
                    <tr>
                      <td colSpan={TABLE_COLUMNS.length + 1} className="px-4 py-6 text-center text-ink-500">
                        Nothing in these results matches "{search}".
                      </td>
                    </tr>
                  )}
                  {visibleRows.map((r) => {
                    const st = STATUS_STYLE[shownStatus(r.verificationStatus)] ?? STATUS_STYLE.UNVERIFIED
                    const place = [r.city, r.state].filter(Boolean).join(', ')
                    return (
                      <tr
                        key={r.id}
                        className={`transition-colors ${selected.has(r.id) ? 'bg-sun-300/15' : 'hover:bg-ink-50/70'}`}
                      >
                        <td className="px-4 py-3 align-top">
                          <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleOne(r.id)} aria-label={`Select ${r.fullName}`} />
                        </td>
                        <td className="px-3 py-3 align-top">
                          <div className="flex items-start gap-2.5">
                            <span
                              className={`grid h-9 w-9 shrink-0 place-items-center rounded-full bg-gradient-to-br ${avatarTone(r.fullName)} text-xs font-bold text-white`}
                              aria-hidden
                            >
                              {initials(r.fullName)}
                            </span>
                            <div className="min-w-0">
                              <button
                                type="button"
                                onClick={() => setEditing(r)}
                                aria-label={`Edit ${r.fullName}`}
                                className="text-left font-semibold leading-tight text-ink-900 underline-offset-2 hover:text-amber-700 hover:underline"
                              >
                                {r.fullName}
                              </button>
                              <div className="mt-1">
                                <span className="rounded-md bg-ink-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-ink-600">
                                  {roleLabel(r.entityKind)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3 align-top text-ink-700">
                          <div className="flex items-center gap-1.5" title={r.email ?? undefined}>
                            <Mail size={13} className="shrink-0 text-ink-400" />
                            <span className="truncate">{r.email ?? '-'}</span>
                          </div>
                          <div className="mt-1 flex items-center gap-1.5 whitespace-nowrap">
                            <Phone size={13} className="shrink-0 text-ink-400" />
                            <span className="tabular-nums">{r.phone ?? '-'}</span>
                          </div>
                        </td>
                        <td className="px-3 py-3 align-top text-ink-700">
                          <div className="flex items-start gap-1.5">
                            <MapPin size={13} className="mt-0.5 shrink-0 text-ink-400" />
                            <div className="min-w-0">
                              <div className="line-clamp-2 break-words">{r.address ?? <span className="text-ink-400">No address</span>}</div>
                              <div className="mt-0.5 text-xs text-ink-500">
                                {place || <span className="text-ink-400">No city</span>}
                                {r.pincode && <span className="ml-1 rounded bg-ink-100 px-1 tabular-nums text-ink-600">{r.pincode}</span>}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3 align-top">
                          {r.categories.length ? (
                            <div className="flex flex-wrap gap-1">
                              {r.categories.map((c) => (
                                <span key={c} className="rounded-full bg-sun-300/40 px-2 py-0.5 text-xs font-semibold text-amber-900">{c}</span>
                              ))}
                            </div>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-rose-600"><AlertTriangle size={12} /> Not set</span>
                          )}
                        </td>
                        <td className="px-3 py-3 align-top">
                          {r.subCategories.length ? (
                            <div className="flex flex-wrap gap-1">
                              {r.subCategories.map((s) => (
                                <span key={s} className="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-800 ring-1 ring-sky-100">{s}</span>
                              ))}
                            </div>
                          ) : (
                            <span className="text-xs text-ink-400">Not set</span>
                          )}
                        </td>
                        <td className="px-3 py-3 align-top">
                          <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${st.pill}`}>
                            <span className={`h-1.5 w-1.5 rounded-full ${st.dot}`} /> {st.label}
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {editing && (
        <EditProviderDialog
          provider={editing}
          onClose={() => setEditing(null)}
          onRecordSaved={(updated) => setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)))}
          onSaved={(updated, serviceChanged) => {
            setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)))
            setEditing(null)
            setNotice(`${updated.fullName}'s record was updated.`)
            // The category and sub category columns come from the service row, which the
            // record update above does not return - reload so the table shows the new ones.
            if (serviceChanged) load()
          }}
        />
      )}
    </div>
  )
}

/** A labelled filter input with its icon inside the box. */
function FilterField({ label, icon, children }: { label: string; icon: ReactNode; children: ReactNode }) {
  return (
    <label className="block">
      <span className="label">{label}</span>
      <span className="relative block">
        <span className="pointer-events-none absolute left-3 top-1/2 z-[1] -translate-y-1/2 text-ink-400">{icon}</span>
        {children}
      </span>
    </label>
  )
}

function EditProviderDialog({
  provider,
  onClose,
  onSaved,
  onRecordSaved,
}: {
  provider: AdminProviderResponse
  onClose: () => void
  onSaved: (updated: AdminProviderResponse, serviceChanged: boolean) => void
  /** The name, contact and address part saved - called even if the category part then fails. */
  onRecordSaved: (updated: AdminProviderResponse) => void
}) {
  const { categories, labelOf, itemsOf } = useTaxonomy()
  // The row carries display labels; the picker and the API want the enum name.
  const currentCategory = categories.find((c) => provider.categories.includes(labelOf(c.name)))
    ?.name as NeedCategory | undefined
  const currentItems = currentCategory
    ? provider.subCategories.filter((s) => itemsOf(currentCategory).includes(s))
    : []
  const [category, setCategory] = useState<NeedCategory | ''>(currentCategory ?? '')
  const [items, setItems] = useState<string[]>(currentItems)
  const [fullName, setFullName] = useState(provider.fullName)
  const [email, setEmail] = useState(provider.email ?? '')
  const [phone, setPhone] = useState(provider.phone ?? '')
  const [addressLine, setAddressLine] = useState('')
  const [city, setCity] = useState(provider.city ?? '')
  const [state, setState] = useState(provider.state ?? '')
  const [pincode, setPincode] = useState(provider.pincode ?? '')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const serviceChanged =
    category !== '' &&
    (category !== currentCategory ||
      items.length !== currentItems.length ||
      items.some((i) => !currentItems.includes(i)))

  async function save() {
    setError(null)
    setSaving(true)
    try {
      // Only what was actually changed. Sending every field back, changed or not, made the server
      // re-check an untouched email against everyone else's - and a person who holds more than one
      // profile shares it with the other one - so saving a category alone could fail on the email.
      const edited = (now: string, before: string | null | undefined) =>
        now.trim() !== (before ?? '').trim() ? now.trim() || undefined : undefined
      const body: AdminProviderUpdateRequest = {
        fullName: edited(fullName, provider.fullName),
        email: edited(email, provider.email),
        phone: edited(phone, provider.phone),
        addressLine: addressLine.trim() || undefined,
        city: edited(city, provider.city),
        state: edited(state, provider.state),
        pincode: edited(pincode, provider.pincode),
      }
      const updated = await adminApi.updateProvider(provider.id, body)
      // Shown in the table now, so a failure below does not leave it showing the old name.
      onRecordSaved(updated)
      if (serviceChanged) {
        // The same call the bulk "Assign category" uses, for one provider: it makes this the primary
        // service through the taxonomy-validated path and keeps the row's other details.
        const res = await adminApi.bulkAssignCategory({
          participantIds: [provider.id],
          category: category as NeedCategory,
          items,
        })
        if (res.failed.length > 0) {
          setError(`Details saved, but the category was not: ${res.failed[0].error}`)
          return
        }
      }
      onSaved(updated, serviceChanged)
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save this record'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[1100] grid place-items-center bg-ink-900/40 p-4">
      <div className="card-pad max-h-[90vh] w-full max-w-md overflow-y-auto bg-white">
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-ink-900">Edit {provider.fullName}</h2>
          <button onClick={onClose} aria-label="Close" className="text-ink-400 hover:text-ink-700">
            <X size={17} />
          </button>
        </div>

        <div className="mt-3 space-y-3">
          <label className="block">
            <span className="label">Full name</span>
            <input value={fullName} onChange={(e) => setFullName(e.target.value)} className="input" />
          </label>
          <label className="block">
            <span className="label">Email</span>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="input" />
          </label>
          <label className="block">
            <span className="label">Phone</span>
            <input value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} className="input" />
          </label>
          <label className="block">
            <span className="label">Address</span>
            <input
              value={addressLine}
              onChange={(e) => setAddressLine(e.target.value)}
              className="input"
              placeholder={provider.address || 'Not set'}
            />
            <span className="mt-1 block text-xs text-ink-400">
              Current: {provider.address || 'not set'}. Leave blank to keep it as is.
            </span>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="label">City</span>
              <input value={city} onChange={(e) => setCity(e.target.value)} className="input" />
            </label>
            <label className="block">
              <span className="label">State</span>
              <StateSelect value={state} onChange={setState} />
            </label>
          </div>
          <label className="block">
            <span className="label">Pincode</span>
            <input value={pincode} onChange={(e) => setPincode(e.target.value.replace(/\D/g, '').slice(0, 6))} className="input" />
          </label>
          <label className="block">
            <span className="label">Category</span>
            <select
              value={category}
              onChange={(e) => { setCategory(e.target.value as NeedCategory); setItems([]) }}
              className="input"
            >
              {category === '' && <option value="">Not set</option>}
              {categories.map((c) => (
                <option key={c.name} value={c.name}>{labelOf(c.name)}</option>
              ))}
            </select>
          </label>
          {category !== '' && (
            <ChipGroup
              label="Sub category (optional)"
              options={itemsOf(category)}
              values={items}
              onToggle={(v) => setItems((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]))}
            />
          )}
        </div>

        <ErrorMessage message={error} />

        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-sun">{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  )
}
